import { inject, Injectable } from '@angular/core';
import { ActivityService } from '@context/frontend/activity';
import {
	collectionCount,
	FIRESTORE,
	ORGANIZATION_ID,
} from '@context/frontend/common';
import { OrganizationConfigService } from '@context/frontend/organization';
import { Fetchable, PaginatedObject } from '@context/frontend/pagination';
import { AuthService } from '@context/frontend/user';
import {
	Chemical,
	ChemicalUsageLog,
	createChemicalUsageLog,
} from '@context/shared/types/chemical';
import { getCutoffDate } from '@context/shared/types/common';
import { sanitize } from '@context/shared/utils';
import {
	collection,
	CollectionReference,
	doc,
	DocumentReference,
	DocumentSnapshot,
	getDoc,
	getDocs,
	limit,
	query,
	setDoc,
	startAfter,
	Timestamp,
	updateDoc,
	where,
} from 'firebase/firestore';
import { firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ChemicalUsageLogService implements Fetchable<ChemicalUsageLog> {
	static readonly CollectionId = 'usageLogs';

	private readonly firestore = inject(FIRESTORE);
	private readonly organizationId = inject(ORGANIZATION_ID);
	private readonly authService = inject(AuthService);
	private readonly activityService = inject(ActivityService);
	private readonly organizationConfigService = inject(
		OrganizationConfigService,
	);

	private getUsageLogsCollection(
		organizationId: string,
	): CollectionReference<ChemicalUsageLog, ChemicalUsageLog> {
		return collection(
			this.firestore,
			'organizations',
			organizationId,
			ChemicalUsageLogService.CollectionId,
		) as CollectionReference<ChemicalUsageLog, ChemicalUsageLog>;
	}

	/**
	 * Creates a new usage log at the organization level.
	 */
	async create(
		chemicalRef: DocumentReference<Chemical>,
		usageLog: Partial<ChemicalUsageLog>,
	): Promise<ChemicalUsageLog> {
		const organizationId = this.organizationId.value;
		if (!organizationId) throw new Error('Organization ID is required');
		if (!chemicalRef.id) throw new Error('Chemical reference is required');

		const payload = createChemicalUsageLog(usageLog) as ChemicalUsageLog;

		const now = Timestamp.now();
		payload.createdAt = now;
		payload.updatedAt = now;
		payload.deletedAt = null;
		payload.id = crypto.randomUUID();
		payload.organizationId = organizationId;
		payload.createdBy = this.authService.userRef!;

		const usageLogRef = doc(
			this.getUsageLogsCollection(organizationId),
			payload.id,
		);
		await setDoc(usageLogRef, sanitize(payload));

		// Log activity for usage log creation
		await this.activityService.create(
			usageLogRef,
			{ type: 'create' },
			this.authService.userRef!,
		);

		return payload;
	}

	/**
	 * Fetches paginated usage logs.
	 * If parentRef (chemicalRef) is provided, fetches usage logs for that chemical.
	 * Otherwise fetches all usage logs for the organization.
	 */
	async fetch(
		paginated?: PaginatedObject<ChemicalUsageLog>,
	): Promise<PaginatedObject<ChemicalUsageLog>> {
		if (!paginated) paginated = new PaginatedObject<ChemicalUsageLog>();

		const organizationId = this.organizationId.value;
		if (!organizationId) {
			throw new Error('Organization ID is required');
		}

		const usageLogsCollection = this.getUsageLogsCollection(organizationId);
		const constraints = [...(paginated.constraints ?? [])];

		// if parent ref is provided then lets show everything, otherwise only show logs within the usage log alive days
		if (paginated?.parentRef) {
			// Filter by chemical when viewing a specific chemical's usage logs
			constraints.unshift(where('chemical', '==', paginated.parentRef));
		} else {
			const aliveDays =
				await this.organizationConfigService.getUsageLogAliveDays();
			const cutoffDate = getCutoffDate(aliveDays);
			constraints.unshift(where('appliedAt', '>=', cutoffDate));
		}

		const usageLogQuery = query(usageLogsCollection);

		if (!paginated.totalElements) {
			paginated.totalElements = await firstValueFrom(
				collectionCount(query(usageLogQuery, ...constraints)),
			);
		}

		if (paginated.page) return paginated;

		const lastDoc = paginated.previousPage?.last ?? null;
		if (lastDoc) constraints.push(startAfter(lastDoc));
		if (paginated.pageSize) constraints.push(limit(paginated.pageSize));

		const snapshot = await getDocs(query(usageLogQuery, ...constraints));

		let last: DocumentSnapshot | null = null;
		const items = snapshot.docs.map((d) => {
			last = d;
			return { data: d.data() as ChemicalUsageLog, ref: d.ref, id: d.id };
		});

		paginated.pages = Object.assign({}, paginated.pages, {
			[paginated.currentPage as number]: { last, items },
		});

		return paginated;
	}

	/**
	 * Gets a specific usage log by ID.
	 */
	async getUsageLog(id: string): Promise<ChemicalUsageLog | null> {
		const organizationId = this.organizationId.value;
		if (!organizationId) return null;

		const usageLogsCollection = this.getUsageLogsCollection(organizationId);
		const usageLogRef = doc(usageLogsCollection, id);
		return getDoc(usageLogRef)
			.then((doc) => doc.data() as ChemicalUsageLog)
			.catch(() => null);
	}

	/**
	 * Updates an existing usage log.
	 */
	async update(
		id: string,
		usageLog: Partial<ChemicalUsageLog>,
	): Promise<ChemicalUsageLog> {
		const organizationId = this.organizationId.value;
		if (!organizationId) throw new Error('Organization ID is required');

		const now = Timestamp.now();
		const payload = createChemicalUsageLog(
			usageLog,
		) as Partial<ChemicalUsageLog>;
		payload.updatedAt = now;

		const usageLogsCollection = this.getUsageLogsCollection(organizationId);
		const usageLogRef = doc(usageLogsCollection, id);
		await updateDoc(usageLogRef, sanitize(payload));

		if (this.authService.userRef) {
			await this.activityService.create(
				usageLogRef,
				{ type: 'update' },
				this.authService.userRef,
			);
		}

		return getDoc(usageLogRef).then(
			(doc) => doc.data() as ChemicalUsageLog,
		);
	}

	/**
	 * Soft deletes a usage log by setting deletedAt timestamp.
	 */
	async delete(id: string): Promise<void> {
		const organizationId = this.organizationId.value;
		if (!organizationId) throw new Error('Organization ID is required');

		const now = Timestamp.now();
		const usageLogsCollection = this.getUsageLogsCollection(organizationId);
		const usageLogRef = doc(usageLogsCollection, id);
		await updateDoc(usageLogRef, { deletedAt: now, updatedAt: now });

		if (this.authService.userRef) {
			await this.activityService.create(
				usageLogRef,
				{ type: 'delete' },
				this.authService.userRef,
			);
		}
	}
}
